import {
    ALL_CONVEYANCERS,
    ALL_PROSPECT_PRACTICE_AREAS,
    ALL_PROVINCES,
    type Conveyancer,
    type ProspectPracticeArea,
    type Province,
} from '@common/enums';
import type { CreateLawFirmPayload, LawFirm, UpdateLawFirmPayload } from '@common/types/lawfirms';
import { getPracticeAreaLabel } from '@common/util';
import { formatConveyancers, getConveyancerLabel } from '@common/utils/conveyancers';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    type SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { type ChangeEvent, useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { createLawFirm, updateLawFirm } from '@/services/LawfirmService';
import { updateLawFirmPrivacy } from '@/services/SupportAccessService';
import { errorSnackbar, successSnackbar, warningSnackbar } from '@/utils/snackbar';

interface FormData {
    companyName: string;
    province: Province;
    conveyancers: Conveyancer[];
    stripeCustomerId: string;
    isTestFirm: boolean;
    isFreeTrial: boolean;
    isPrivate: boolean;
    freeTrialPracticeAreas: ProspectPracticeArea[];
    quoteIntakeBasePrice: string;
    intakeOnlyBasePrice: string;
    idvBasePrice: string;
}

interface Props {
    lawFirm?: LawFirm;
    onClose(): void;
}

function optionalNumber(value: string): number | undefined {
    const trimmedValue = value.trim();
    return trimmedValue ? Number(trimmedValue) : undefined;
}

function priceToFormValue(value: number | string | null | undefined, fallback: string) {
    return value == null ? fallback : String(value);
}

export default function CreateLawFirmDialog({ lawFirm, onClose }: Props) {
    const queryClient = useQueryClient();
    const isEditMode = !!lawFirm;
    const [form, setForm] = useState<FormData>({
        companyName: lawFirm?.companyName ?? '',
        province: lawFirm?.province ?? 'Ontario',
        conveyancers: lawFirm?.conveyancers ?? ['unity'],
        stripeCustomerId: lawFirm?.stripeCustomerId ?? '',
        isTestFirm: lawFirm?.isTestFirm ?? false,
        isFreeTrial: lawFirm?.isFreeTrial ?? false,
        isPrivate: lawFirm?.isPrivate ?? false,
        freeTrialPracticeAreas: lawFirm?.freeTrialPracticeAreas ?? [],
        quoteIntakeBasePrice: priceToFormValue(lawFirm?.quoteIntakeBasePrice, '25.00'),
        intakeOnlyBasePrice: priceToFormValue(lawFirm?.intakeOnlyBasePrice, '20.00'),
        idvBasePrice: priceToFormValue(lawFirm?.idvBasePrice, '25.00'),
    });
    const [companyNameError, setCompanyNameError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isConfirmingNotPrivate, setIsConfirmingNotPrivate] = useState(false);

    const handleTextChange = (field: keyof FormData) => (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));

        if (field === 'companyName' && value.trim()) {
            setCompanyNameError('');
        }
    };

    const handleSave = async (isNotPrivateConfirmed = false) => {
        const companyName = form.companyName.trim();
        if (!companyName) {
            setCompanyNameError('Company name is required');
            return;
        }
        const isPrivacyChanged = form.isPrivate !== (lawFirm?.isPrivate ?? false);
        if (isPrivacyChanged && !form.isPrivate && !isNotPrivateConfirmed) {
            setIsConfirmingNotPrivate(true);
            return;
        }
        setIsConfirmingNotPrivate(false);

        setIsSaving(true);
        try {
            const sharedPayload = {
                companyName,
                province: form.province || undefined,
                conveyancers: form.conveyancers,
                isTestFirm: form.isTestFirm,
                isFreeTrial: form.isFreeTrial,
                freeTrialPracticeAreas: form.freeTrialPracticeAreas,
                quoteIntakeBasePrice: optionalNumber(form.quoteIntakeBasePrice),
                intakeOnlyBasePrice: optionalNumber(form.intakeOnlyBasePrice),
                idvBasePrice: optionalNumber(form.idvBasePrice),
            };
            const stripeCustomerId = form.stripeCustomerId.trim();

            let lawFirmId: number;
            if (lawFirm) {
                const payload = {
                    ...sharedPayload,
                    stripeCustomerId: stripeCustomerId || null,
                } satisfies UpdateLawFirmPayload;
                await updateLawFirm(lawFirm.lawFirmId, payload);
                lawFirmId = lawFirm.lawFirmId;
            } else {
                const payload = {
                    ...sharedPayload,
                    stripeCustomerId: stripeCustomerId || undefined,
                } satisfies CreateLawFirmPayload;
                lawFirmId = (await createLawFirm(payload)).lawFirmId;
            }
            // Privacy has its own route, which records the change in the firm's audit log. The
            // firm is saved by now, so a failure here closes the dialog and says what's left to do
            let isPrivacySaved = true;
            if (isPrivacyChanged) {
                try {
                    await updateLawFirmPrivacy(lawFirmId, form.isPrivate);
                } catch (error: unknown) {
                    console.error('Error updating law firm privacy:', error);
                    isPrivacySaved = false;
                }
            }

            await queryClient.invalidateQueries({ queryKey: ['lawFirms'] });
            await queryClient.invalidateQueries({ queryKey: ['lawFirm', lawFirmId] });
            await queryClient.invalidateQueries({ queryKey: ['supportAccess', lawFirmId] });
            if (isPrivacySaved) {
                successSnackbar(`Law firm ${isEditMode ? 'updated' : 'created'} successfully.`);
            } else {
                errorSnackbar(
                    `Law firm ${isEditMode ? 'updated' : 'created'}, but it couldn't be made ${form.isPrivate ? 'private' : 'not private'}. Edit the firm to try again.`,
                );
            }
            onClose();
        } catch (error: unknown) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} law firm:`, error);
            if (isAxiosError(error) && error.response?.status === 409) {
                warningSnackbar('A law firm with this company name already exists.');
            } else {
                const errorMessage = isAxiosError(error)
                    ? (error.response?.data as string)
                    : `Failed to ${isEditMode ? 'update' : 'create'} law firm. Please try again.`;
                errorSnackbar(errorMessage);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleConveyancersChange = (event: SelectChangeEvent<Conveyancer[]>) => {
        const value = event.target.value;
        setForm((prev) => ({
            ...prev,
            conveyancers: typeof value === 'string' ? (value.split(',') as Conveyancer[]) : value,
        }));
    };

    const handleTrialPracticeAreasChange = (event: SelectChangeEvent<ProspectPracticeArea[]>) => {
        const value = event.target.value;
        setForm((prev) => ({
            ...prev,
            freeTrialPracticeAreas:
                typeof value === 'string' ? (value.split(',') as ProspectPracticeArea[]) : value,
        }));
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            onClose={(_event, reason) => {
                if (reason === 'backdropClick' && isSaving) return;
                onClose();
            }}
        >
            <DialogTitle>{isEditMode ? 'Edit Law Firm' : 'Create Law Firm'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 3, color: '#7B7D80' }}>
                    Company name is required. Other settings can be filled in now or updated later.
                </Typography>
                <Stack spacing={2}>
                    <TextField
                        autoFocus
                        label="Company Name"
                        fullWidth
                        required
                        value={form.companyName}
                        onChange={handleTextChange('companyName')}
                        onBlur={() => {
                            if (!form.companyName.trim()) {
                                setCompanyNameError('Company name is required');
                            }
                        }}
                        disabled={isSaving}
                        error={!!companyNameError}
                        helperText={companyNameError}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="law-firm-province">Province</InputLabel>
                        <Select
                            labelId="law-firm-province"
                            label="Province"
                            value={form.province}
                            onChange={(event) =>
                                setForm((prev) => ({
                                    ...prev,
                                    province: event.target.value as Province,
                                }))
                            }
                            disabled={isSaving}
                        >
                            {ALL_PROVINCES.map((province) => (
                                <MenuItem key={province} value={province}>
                                    {province}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel id="law-firm-conveyancers">Conveyancers</InputLabel>
                        <Select
                            labelId="law-firm-conveyancers"
                            label="Conveyancers"
                            multiple
                            value={form.conveyancers}
                            onChange={handleConveyancersChange}
                            input={<OutlinedInput label="Conveyancers" />}
                            renderValue={(selected) => formatConveyancers(selected)}
                            disabled={isSaving}
                        >
                            {ALL_CONVEYANCERS.map((conveyancer) => (
                                <MenuItem key={conveyancer} value={conveyancer}>
                                    <Checkbox checked={form.conveyancers.includes(conveyancer)} />
                                    <ListItemText primary={getConveyancerLabel(conveyancer)} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Stripe Customer ID"
                        fullWidth
                        value={form.stripeCustomerId}
                        onChange={handleTextChange('stripeCustomerId')}
                        disabled={isSaving}
                    />
                    <FormControlLabel
                        sx={{ alignItems: 'flex-start', ml: 0 }}
                        control={
                            <Checkbox
                                checked={form.isTestFirm}
                                onChange={(event) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        isTestFirm: event.target.checked,
                                    }))
                                }
                                disabled={isSaving}
                                sx={{ py: 0.25 }}
                            />
                        }
                        label={
                            <Stack spacing={0.25}>
                                <Typography>Test Firm</Typography>
                                <Typography variant="caption" sx={{ color: '#7B7D80' }}>
                                    Lexo internal firm. Always excluded from billing, hidden from
                                    the default firm list, and connected to GoVeyance staging. Also
                                    unlocks extra matter types used for internal testing.
                                </Typography>
                            </Stack>
                        }
                    />

                    <FormControlLabel
                        sx={{ alignItems: 'flex-start', ml: 0 }}
                        control={
                            <Checkbox
                                checked={form.isPrivate}
                                onChange={(event) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        isPrivate: event.target.checked,
                                    }))
                                }
                                disabled={isSaving}
                                sx={{ py: 0.25 }}
                            />
                        }
                        label={
                            <Stack spacing={0.25}>
                                <Typography>Private Firm</Typography>
                                <Typography variant="caption" sx={{ color: 'neutral.N_500' }}>
                                    Only for firms whose agreement includes privacy. Lexo Admins
                                    can't see the firm's matters, contacts or ID verifications
                                    unless one of its admins gives Lexo access, under Settings, Lexo
                                    Access. Firm setup, users and billing stay visible.
                                </Typography>
                            </Stack>
                        }
                    />

                    <Stack sx={{ width: '100%', minWidth: 0 }}>
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{ width: '100%', minWidth: 0 }}
                        >
                            <FormControlLabel
                                sx={{ flexShrink: 0, ml: 0 }}
                                control={
                                    <Checkbox
                                        checked={form.isFreeTrial}
                                        onChange={(event) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                isFreeTrial: event.target.checked,
                                            }))
                                        }
                                        disabled={isSaving}
                                    />
                                }
                                label="Free Trial"
                            />
                            <FormControl sx={{ flex: 1, minWidth: 0, width: 0 }}>
                                <InputLabel id="law-firm-trial-practice-areas">
                                    Trial practice areas
                                </InputLabel>
                                <Select
                                    labelId="law-firm-trial-practice-areas"
                                    label="Trial practice areas"
                                    multiple
                                    fullWidth
                                    value={form.freeTrialPracticeAreas}
                                    onChange={handleTrialPracticeAreasChange}
                                    input={<OutlinedInput label="Trial practice areas" />}
                                    renderValue={(selected) =>
                                        selected.length === 0
                                            ? form.isFreeTrial
                                                ? 'All'
                                                : ''
                                            : selected
                                                  .map((area) => getPracticeAreaLabel(area))
                                                  .join(', ')
                                    }
                                    disabled={isSaving || !form.isFreeTrial}
                                >
                                    {ALL_PROSPECT_PRACTICE_AREAS.map((practiceArea) => (
                                        <MenuItem key={practiceArea} value={practiceArea}>
                                            <Checkbox
                                                checked={form.freeTrialPracticeAreas.includes(
                                                    practiceArea,
                                                )}
                                            />
                                            <ListItemText
                                                primary={getPracticeAreaLabel(practiceArea)}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#7B7D80', mt: 0.5 }}>
                            Prospects created during the trial are billed at $0. Leave empty for all
                            practice areas, or select areas to limit the trial. IDV is billed
                            separately and cannot be trialed.
                        </Typography>
                    </Stack>
                    <TextField
                        label="Quote & Intake Price"
                        type="number"
                        fullWidth
                        value={form.quoteIntakeBasePrice}
                        onChange={handleTextChange('quoteIntakeBasePrice')}
                        disabled={isSaving}
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                    <TextField
                        label="Intake Price"
                        type="number"
                        fullWidth
                        value={form.intakeOnlyBasePrice}
                        onChange={handleTextChange('intakeOnlyBasePrice')}
                        disabled={isSaving}
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                    <TextField
                        label="IDV Price"
                        type="number"
                        fullWidth
                        value={form.idvBasePrice}
                        onChange={handleTextChange('idvBasePrice')}
                        disabled={isSaving}
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={() => handleSave()}
                    loading={isSaving}
                    variant="contained"
                    disabled={
                        !form.companyName.trim() || form.conveyancers.length === 0 || isSaving
                    }
                >
                    {isEditMode ? 'Save' : 'Create'}
                </LoadingButton>
            </DialogActions>
            <AlertDialog
                open={isConfirmingNotPrivate}
                title="Make this firm not private?"
                message={`Lexo Admins will see all of ${form.companyName.trim() || 'this firm'}'s client data, and any access the firm has given ends. The change is recorded in the firm's audit log, which its admins can see.`}
                isLoading={isSaving}
                handleClose={() => setIsConfirmingNotPrivate(false)}
                handleConfirm={() => handleSave(true)}
            />
        </Dialog>
    );
}
